import { useState } from 'react';
import { PREDEFINED_DOCS, PredefinedDoc } from '../../../constants/required-docs.constants';
import { styles } from '../../../styles/required-docs.styles';
import { DocCard } from './DocCard';
import { CustomDocForm } from './CustomDocForm';
import { DocsSummary } from './DocsSummary';

export interface SelectedDoc {
  key: string;
  label: string;
  description: string;
  acceptedFormats: string;
  isRequired: boolean;
  isCustom?: boolean;
}

interface RequiredDocsSelectorProps {
  selectedDocs: SelectedDoc[];
  onChange: (docs: SelectedDoc[]) => void;
}

export function RequiredDocsSelector({ selectedDocs, onChange }: RequiredDocsSelectorProps) {
  const [showCustomForm, setShowCustomForm] = useState(false);
  const selectedKeys = new Set(selectedDocs.map((d) => d.key));

  function togglePredefined(doc: PredefinedDoc) {
    if (selectedKeys.has(doc.key)) {
      onChange(selectedDocs.filter((d) => d.key !== doc.key));
    } else {
      onChange([
        ...selectedDocs,
        {
          key: doc.key,
          label: doc.label,
          description: doc.description,
          acceptedFormats: doc.formats,
          isRequired: doc.defaultRequired,
        },
      ]);
    }
  }

  function toggleRequired(key: string) {
    onChange(selectedDocs.map((d) => (d.key === key ? { ...d, isRequired: !d.isRequired } : d)));
  }

  function removeDoc(key: string) {
    onChange(selectedDocs.filter((d) => d.key !== key));
  }

  function addCustomDoc(doc: SelectedDoc) {
    onChange([...selectedDocs, doc]);
    setShowCustomForm(false);
  }

  return (
    <div style={styles.container}>
      <div>
        <div style={styles.sectionTitle}>Documents predefinies</div>
        <div style={styles.sectionSubtitle}>Cliquez pour selectionner les documents requis pour ce type d'espace</div>
        <div style={styles.grid}>
          {PREDEFINED_DOCS.map((doc) => (
            <DocCard
              key={doc.key}
              doc={doc}
              isSelected={selectedKeys.has(doc.key)}
              onToggle={() => togglePredefined(doc)}
            />
          ))}
        </div>
      </div>

      <div>
        {!showCustomForm ? (
          <button
            type="button"
            style={styles.addBtn}
            onClick={() => setShowCustomForm(true)}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0,117,141,0.05)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            + Autre document
          </button>
        ) : (
          <CustomDocForm onAdd={addCustomDoc} onCancel={() => setShowCustomForm(false)} />
        )}
      </div>

      {selectedDocs.length > 0 && <div style={styles.separator} />}

      <DocsSummary 
        selectedDocs={selectedDocs} 
        onToggleRequired={toggleRequired} 
        onRemove={removeDoc} 
      />

      {selectedDocs.length === 0 && (
        <div style={styles.emptyState}>
          Aucun document selectionne. Cliquez sur les cartes ci-dessus pour ajouter des documents requis.
        </div>
      )}
    </div>
  );
}
